import { useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { getQueryFn } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Loader2 } from "lucide-react";
import {
  contractStatusSchema,
  contractTypeValues,
  renewalModeValues,
  contractUpsertSchema,
} from "@shared/contracts/commercial";

type ClientOption = { id: string; name: string };
type AccountManagerOption = {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
};

const formSchema = contractUpsertSchema.extend({
  startDate: contractUpsertSchema.shape.startDate.transform((date) =>
    date.toISOString().slice(0, 10),
  ),
  endDate: contractUpsertSchema.shape.endDate
    .transform((date) => (date ? date.toISOString().slice(0, 10) : ""))
    .optional(),
  contractOwnerUserId: contractUpsertSchema.shape.contractOwnerUserId.transform(
    (value) => value ?? "inherit",
  ),
  annualPriceReviewMonth: contractUpsertSchema.shape.annualPriceReviewMonth
    .transform((value) => (value ? String(value) : ""))
    .optional(),
  noticePeriodDays: contractUpsertSchema.shape.noticePeriodDays
    .transform((value) => (value === null || value === undefined ? "" : String(value)))
    .optional(),
});

type FormValues = {
  clientId: string;
  name: string;
  contractOwnerUserId: string;
  contractType: (typeof contractTypeValues)[number];
  startDate: string;
  endDate?: string;
  renewalMode: (typeof renewalModeValues)[number];
  noticePeriodDays?: string;
  annualPriceReviewMonth?: string;
  status: ReturnType<typeof contractStatusSchema.parse>;
  description?: string | null;
};

interface ContractFormProps {
  defaultValues?: Partial<FormValues>;
  onSubmit: (data: Record<string, unknown>) => void;
  isPending?: boolean;
}

export function ContractForm({
  defaultValues,
  onSubmit,
  isPending,
}: ContractFormProps) {
  const { data: clients } = useQuery<ClientOption[]>({
    queryKey: ["/api/clients"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const { data: accountManagers } = useQuery<AccountManagerOption[]>({
    queryKey: ["/api/commercial/account-managers"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema as any),
    defaultValues: {
      clientId: "",
      name: "",
      contractOwnerUserId: "inherit",
      contractType: "rolling_month_to_month",
      startDate: "",
      endDate: "",
      renewalMode: "manual_review",
      noticePeriodDays: "30",
      annualPriceReviewMonth: "",
      status: "draft",
      description: "",
      ...defaultValues,
    },
  });

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((values) =>
          onSubmit({
            ...values,
            contractOwnerUserId:
              values.contractOwnerUserId === "inherit"
                ? null
                : values.contractOwnerUserId,
            endDate: values.endDate || null,
            annualPriceReviewMonth: values.annualPriceReviewMonth
              ? Number(values.annualPriceReviewMonth)
              : null,
            noticePeriodDays: values.noticePeriodDays
              ? Number(values.noticePeriodDays)
              : null,
          }),
        )}
        className="space-y-4"
      >
        <FormField
          control={form.control}
          name="clientId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Client</FormLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {(clients ?? []).map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Contract name</FormLabel>
              <FormControl>
                <Input placeholder="Managed Services Agreement" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="contractType"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Contract type</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {contractTypeValues.map((value) => (
                      <SelectItem key={value} value={value}>
                        {value.replace(/_/g, " ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Status</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {["draft", "active", "pending_signature", "expired", "terminated"].map(
                      (value) => (
                        <SelectItem key={value} value={value}>
                          {value.replace(/_/g, " ")}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="startDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Start date</FormLabel>
                <FormControl>
                  <Input type="date" {...field} />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="endDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>End date</FormLabel>
                <FormControl>
                  <Input type="date" {...field} value={field.value ?? ""} />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <FormField
            control={form.control}
            name="renewalMode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Renewal mode</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {renewalModeValues.map((value) => (
                      <SelectItem key={value} value={value}>
                        {value.replace(/_/g, " ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="noticePeriodDays"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Notice days</FormLabel>
                <FormControl>
                  <Input type="number" min="0" {...field} value={field.value ?? ""} />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="annualPriceReviewMonth"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Price review month</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min="1"
                    max="12"
                    {...field}
                    value={field.value ?? ""}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="contractOwnerUserId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Contract owner</FormLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Inherit from client account manager" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="inherit">
                    Inherit from client account manager
                  </SelectItem>
                  {(accountManagers ?? []).map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {[user.firstName, user.lastName].filter(Boolean).join(" ") ||
                        user.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  value={field.value ?? ""}
                  className="min-h-[90px]"
                  placeholder="Optional commercial notes"
                />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="flex justify-end">
          <Button type="submit" disabled={isPending}>
            {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save contract
          </Button>
        </div>
      </form>
    </Form>
  );
}
